import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Vector3 } from 'three'
import { v4 as uuid } from 'uuid'
import { Box, Stack, Typography } from '@mui/material'
import { useEditElementStore } from '@editor/stores/editElementStore'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AddButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { useResultsInvalidation } from '@editorHooks'
import { useModelInvalidation, useStructuralPlanningSelection } from '@structuralPlanningHooks'
import { getModel } from '@queries'
import { createOpening } from '@mutations'
import OpeningForm from '../OpeningForm'
import OpeningSceneMarker from './components/openingSceneMarker'

interface Props {
  wall: ShapeObject
  mode: '2D' | '3D'
  /**
   * Overrides "in Draufsicht bearbeiten" (e.g. to switch the IFC importer's own `draw-openings`
   * `ifcMode` instead of Structural Planning's mode, which the IFC import page doesn't react to).
   * Defaults to Structural Planning's own mode switch.
   */
  onEditInTopView?: (wallGuid: string) => void
}

const WallOpeningsSection = ({ wall, mode, onEditInTopView }: Props): ReactElement | null => {
  const { enqueueSnackbar } = useSnackbar()
  const setActiveElement = useEditElementStore(state => state.setActiveElement)
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const invalidateResults = useResultsInvalidation()
  const invalidateModel = useModelInvalidation()
  const {
    actions: { setElements, toggleMode },
  } = useStructuralPlanningSelection()

  const [selectedOpeningGuid, setSelectedOpeningGuid] = useState<string | null>(() =>
    wall.openings && wall.openings.length > 0 ? wall.openings[0].guid : null,
  )

  useEffect(() => {
    if (!wall.openings || wall.openings.length === 0) {
      setSelectedOpeningGuid(null)
      return
    }

    if (!selectedOpeningGuid || !wall.openings.some(o => o.guid === selectedOpeningGuid)) {
      setSelectedOpeningGuid(wall.openings[0].guid)
    }
  }, [wall.openings, selectedOpeningGuid])

  const sortedOpenings = useMemo(() => wall.openings ?? [], [wall.openings])

  const wallStart = wall.shape.points[0]
  const wallEnd = wall.shape.points[1]

  const wallAxis = useMemo(() => {
    const start = new Vector3(wallStart.x, wallStart.y, wallStart.z)
    const end = new Vector3(wallEnd.x, wallEnd.y, wallEnd.z)
    return end.sub(start).normalize()
  }, [wallStart, wallEnd])

  const wallLength = useMemo(() => {
    const start = new Vector3(wallStart.x, wallStart.y, wallStart.z)
    const end = new Vector3(wallEnd.x, wallEnd.y, wallEnd.z)
    return start.distanceTo(end)
  }, [wallStart, wallEnd])

  const { minZ, maxZ } = useMemo(() => {
    const zs = wall.shape.points.map(p => p.z)
    return {
      minZ: Math.min(...zs),
      maxZ: Math.max(...zs),
    }
  }, [wall.shape.points])

  const { mutateAsync: createOpeningMutation, isLoading: isCreating } = useMutation(
    async (points: ImmutableVector3[]) => {
      if (!projectId) return

      const openingGuid = uuid()

      await createOpening.request(projectId, {
        element_guid: wall.guid,
        shape: {
          guid: openingGuid,
          points,
        },
      })

      setElements([])
      await Promise.all([
        invalidateModel(projectId),
        queryClient.invalidateQueries(getModel.getKey(projectId)),
      ])

      invalidateResults(projectId)

      enqueueSnackbar('Öffnung erfolgreich erstellt', { variant: 'success' })

      return openingGuid
    },
  )

  const projectPointOnWall = useCallback(
    (distanceAlong: number, z: number) => {
      const start = new Vector3(wallStart.x, wallStart.y, wallStart.z)
      const pointOnAxis = start.add(wallAxis.clone().multiplyScalar(distanceAlong))
      return new ImmutableVector3(pointOnAxis.x, pointOnAxis.y, z)
    },
    [wallStart, wallAxis],
  )

  const getOpeningInterval = useCallback(
    (opening: Opening) => {
      const start = opening.shape.points[0]
      const end = opening.shape.points[2]

      const wallStartVec = new Vector3(wallStart.x, wallStart.y, wallStart.z)
      const startVec = new Vector3(start.x, start.y, start.z)
      const endVec = new Vector3(end.x, end.y, end.z)

      const startDist = startVec.sub(wallStartVec).dot(wallAxis)
      const endDist = endVec.sub(wallStartVec).dot(wallAxis)

      return { startDist, endDist }
    },
    [wallAxis, wallStart],
  )

  const createOpeningWithExtents = useCallback(
    async (openingStart: number, openingEnd: number) => {
      if (!projectId || openingEnd <= openingStart) {
        enqueueSnackbar('Nicht genug Platz für eine neue Öffnung', { variant: 'warning' })
        return
      }

      const bottomZ = minZ + 0.5
      const topZ = maxZ - 0.5

      const p1Bottom = projectPointOnWall(openingStart, bottomZ)
      const p2Bottom = projectPointOnWall(openingEnd, bottomZ)
      const p2Top = projectPointOnWall(openingEnd, topZ)
      const p1Top = projectPointOnWall(openingStart, topZ)

      const newGuid = await createOpeningMutation([p1Bottom, p2Bottom, p2Top, p1Top])

      if (newGuid) {
        setSelectedOpeningGuid(newGuid)
      }
    },
    [projectId, minZ, maxZ, projectPointOnWall, createOpeningMutation, enqueueSnackbar],
  )

  const createOpeningInGap = useCallback(
    async (gapStart: number, gapEnd: number) => {
      const minGap = 0.2
      const openingStart = gapStart + minGap
      const openingEnd = gapEnd - minGap
      await createOpeningWithExtents(openingStart, openingEnd)
    },
    [createOpeningWithExtents],
  )

  const handleAddAtStart = useCallback(async () => {
    if (sortedOpenings.length === 0) {
      await createOpeningInGap(0, wallLength)
      return
    }

    const first = sortedOpenings[0]
    const { startDist } = getOpeningInterval(first)

    await createOpeningInGap(0, startDist)
  }, [sortedOpenings, createOpeningInGap, getOpeningInterval, wallLength])

  const handleAddAtEnd = useCallback(async () => {
    if (sortedOpenings.length === 0) {
      await createOpeningInGap(0, wallLength)
      return
    }

    const last = sortedOpenings[sortedOpenings.length - 1]
    const { endDist } = getOpeningInterval(last)

    await createOpeningInGap(endDist, wallLength)
  }, [sortedOpenings, createOpeningInGap, getOpeningInterval, wallLength])

  const handleAddBetween = useCallback(
    async (index: number) => {
      const current = sortedOpenings[index]
      const next = sortedOpenings[index + 1]

      if (!current || !next) return

      const { endDist: currentEnd } = getOpeningInterval(current)
      const { startDist: nextStart } = getOpeningInterval(next)

      await createOpeningInGap(currentEnd, nextStart)
    },
    [sortedOpenings, createOpeningInGap, getOpeningInterval],
  )

  const handleAddInitial = useCallback(async () => {
    if (sortedOpenings.length > 0) return

    const desiredLength = 0.9
    const requiredLengthWithGaps = desiredLength + 0.4

    if (wallLength >= requiredLengthWithGaps) {
      const openingCenter = wallLength / 2
      const openingStart = openingCenter - desiredLength / 2
      const openingEnd = openingCenter + desiredLength / 2
      await createOpeningWithExtents(openingStart, openingEnd)
      return
    }

    const openingStart = 0.2
    const openingEnd = wallLength - 0.2
    await createOpeningWithExtents(openingStart, openingEnd)
  }, [sortedOpenings.length, wallLength, createOpeningWithExtents])

  const handleDeselectWall = useCallback(() => {
    if (mode === '2D') {
      setActiveElement(null)
    } else if (mode === '3D') {
      setElements([])
    }
  }, [mode, setActiveElement, setElements])

  const selectedOpening = useMemo(
    () => sortedOpenings.find(o => o.guid === selectedOpeningGuid),
    [sortedOpenings, selectedOpeningGuid],
  )

  return (
    <Stack direction="column" spacing={1} alignItems="center">
      {sortedOpenings.map(opening => (
        <OpeningSceneMarker
          key={opening.guid}
          opening={opening}
          isSelected={opening.guid === selectedOpeningGuid}
          onSelect={setSelectedOpeningGuid}
        />
      ))}
      <Stack direction="row" spacing={1} alignItems="center">
        {sortedOpenings.length === 0 ? (
          <AddButton
            size="small"
            onClick={handleAddInitial}
            disabled={isCreating}
            sx={{ minWidth: 32 }}
          >
            Öffnung erstellen
          </AddButton>
        ) : (
          <>
            <AddButton
              size="small"
              onClick={handleAddAtStart}
              disabled={isCreating}
              sx={{ minWidth: 32 }}
            />
            {sortedOpenings.map((opening, index) => {
              const isSelected = opening.guid === selectedOpeningGuid
              return (
                <Stack key={opening.guid} direction="row" spacing={1} alignItems="center">
                  <Box
                    onClick={() => setSelectedOpeningGuid(opening.guid)}
                    sx={{
                      cursor: 'pointer',
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'primary.light' : 'background.paper',
                      minWidth: 32,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" fontWeight={isSelected ? 700 : 400}>
                      {index + 1}
                    </Typography>
                  </Box>
                  {index < sortedOpenings.length - 1 && (
                    <AddButton
                      size="small"
                      onClick={() => handleAddBetween(index)}
                      disabled={isCreating}
                      sx={{ minWidth: 32 }}
                    />
                  )}
                </Stack>
              )
            })}
            <AddButton
              size="small"
              onClick={handleAddAtEnd}
              disabled={isCreating}
              sx={{ minWidth: 32 }}
            />
          </>
        )}
      </Stack>

      {selectedOpeningGuid && selectedOpening && (
        <>
          <OpeningForm
            selectedWallId={wall.guid}
            selectedOpeningId={selectedOpeningGuid}
            deselectWall={handleDeselectWall}
            setElements={setElements}
            toggleMode={onEditInTopView ? () => onEditInTopView(wall.guid) : toggleMode}
            mode={mode}
          />
        </>
      )}
    </Stack>
  )
}

export default WallOpeningsSection
